import { DragEvent, FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import { useMutation } from "convex/react";
import { api, Doc, Id } from "@/smart-views/client/api";
import { BlockShell, type BlockStatusWorkItem } from "./blocks/BlockShell";
import { MarkdownBlock } from "./blocks/MarkdownBlock";
import { KeyValueBlock } from "./blocks/KeyValueBlock";
import { TaskListBlock } from "./blocks/TaskListBlock";
import { TableBlock } from "./blocks/TableBlock";
import {
  CalendarEventsBlock,
  CardGridBlock,
  CollectionShortcutBlock,
  DateTextLinkBlock,
  DayAgendaBlock,
  ExpandedNoteBlock,
  MonthCalendarBlock,
  MultiImageBlock,
  NoteListBlock,
  NoteShortcutBlock,
  ResourceListBlock,
  ResourceShortcutBlock,
  SingleImageBlock,
  TimelineBlock,
  WeekCalendarBlock,
  YearCalendarBlock,
} from "./blocks/StructuredBlocks";
import { BlockInsertionRow } from "./blocks/BlockInsertionRow";
import { BlockDraft } from "./blocks/BlockDraft";
import { SkeletonBlock } from "./blocks/SkeletonBlock";
import { chatWithSmartView } from "@/smart-views/client/backend-api";
import { useNotifyAgent } from "@/smart-views/client/use-notify-agent";
import { logger } from "@/modules/logger";
import { iconForBlockType, BlockType } from "./blocks/blockTypes";
import {
  buildSmartViewLayoutRows,
  canDropBlockOnEmptyHalfSlot,
  getSmartViewEmptyHalfSlotTargetBlockId,
  getSmartViewBlockDropPlacement,
  type SmartViewBlockDropPlacement,
  SMART_VIEW_LAYOUT_COLUMNS,
  SMART_VIEW_HALF_WIDTH,
} from "./blocks/layout";

interface SmartViewDashboardProps {
  smartViewId: string;
  blocks: Doc<"blocks">[];
  blockVersions: Doc<"blockVersions">[];
  workItems?: Doc<"smartViewWorkItems">[];
  layout: Doc<"smartViewVersions">["layout"];
  isReadOnly?: boolean;
  changedBlockIds?: Id<"blocks">[];
}

const DRAG_SCROLL_EDGE_SIZE = 96;
const DRAG_SCROLL_BASE_MAX_PX_PER_FRAME = 14;
const DRAG_SCROLL_SPEED_MULTIPLIER = 2;
const DRAG_SCROLL_MAX_PX_PER_FRAME =
  DRAG_SCROLL_BASE_MAX_PX_PER_FRAME * DRAG_SCROLL_SPEED_MULTIPLIER;

export const SmartViewDashboard: FC<SmartViewDashboardProps> = ({
  smartViewId,
  blocks,
  blockVersions,
  workItems = [],
  layout,
  isReadOnly = false,
  changedBlockIds = [],
}) => {
  const addBlock = useMutation(api.smartViews.client.addBlock);
  const moveBlockBesideFullBlock = useMutation(api.smartViews.client.moveBlockBesideFullBlock);
  const moveBlockBefore = useMutation(api.smartViews.client.moveBlockBefore);
  const moveBlockToFullRow = useMutation(api.smartViews.client.moveBlockToFullRow);
  const moveBlockToFullRowBeforeBlock = useMutation(
    api.smartViews.client.moveBlockToFullRowBeforeBlock
  );
  const moveBlockToEmptyHalfSlot = useMutation(api.smartViews.client.moveBlockToEmptyHalfSlot);
  const stackRef = useRef<HTMLDivElement | null>(null);
  const dragClientYRef = useRef<number | null>(null);
  const dragScrollFrameRef = useRef<number | null>(null);
  const notifyAgent = useNotifyAgent(smartViewId);
  const addPlaceholderBlock = useMutation(api.smartViews.client.addPlaceholderBlock);
  const updatePlaceholderDescription = useMutation(
    api.smartViews.client.updatePlaceholderDescription
  );
  const cancelPlaceholderBlock = useMutation(api.smartViews.client.cancelPlaceholderBlock);

  const addPlaceholderAt = async (insertAtRow: number) => {
    try {
      await addPlaceholderBlock({ smartViewId, insertAtRow });
    } catch (err) {
      logger.error({
        message: "Smart View: failed to add placeholder block",
        info: { err: String(err) },
      });
    }
  };

  const submitPlaceholder = async (blockId: Id<"blocks">, description: string) => {
    try {
      await updatePlaceholderDescription({ blockId, description });
      await chatWithSmartView({
        smartViewId,
        message:
          `There is a placeholder block (id: ${blockId}) waiting to be fulfilled ` +
          `with this description:\n\n"${description}"\n\n` +
          `Call \`fulfill_placeholder_block\` with block_id "${blockId}" to ` +
          `replace it in place. Pick the block type that best fits and pass ` +
          `the full content for that type. ` +
          `Do NOT call \`add_block\` — that appends at the bottom and orphans ` +
          `the placeholder. \`fulfill_placeholder_block\` preserves the slot ` +
          `where the user placed the placeholder and clears the placeholder flag.`,
      });
    } catch (err) {
      logger.error({
        message: "Smart View: failed to submit placeholder",
        info: { err: String(err) },
      });
    }
  };

  const cancelPlaceholder = async (blockId: Id<"blocks">) => {
    try {
      await cancelPlaceholderBlock({ blockId });
    } catch (err) {
      logger.error({
        message: "Smart View: failed to cancel placeholder",
        info: { err: String(err) },
      });
    }
  };

  const [draggedBlockId, setDraggedBlockId] = useState<Id<"blocks"> | null>(null);
  const [dropTargetBlock, setDropTargetBlock] = useState<{
    blockId: Id<"blocks">;
    placement: SmartViewBlockDropPlacement;
  } | null>(null);
  const [dropTargetEmptyHalfBlockId, setDropTargetEmptyHalfBlockId] = useState<Id<"blocks"> | null>(
    null
  );

  const rows = useMemo(
    () =>
      buildSmartViewLayoutRows({
        layout,
        blocks,
        blockVersions,
      }),
    [blockVersions, blocks, layout]
  );
  const changedBlockIdSet = useMemo(() => new Set(changedBlockIds), [changedBlockIds]);
  const blockWorkItemState = useMemo(() => buildBlockWorkItemState(workItems), [workItems]);

  const insertAt = (insertAtRow: number) => (type: BlockType) => {
    void addBlock({ smartViewId, type, insertAtRow });
  };

  const updateDragScrollPosition = useCallback((clientY: number) => {
    if (clientY > 0) {
      dragClientYRef.current = clientY;
    }
  }, []);

  const stopDragAutoScroll = useCallback(() => {
    if (dragScrollFrameRef.current !== null) {
      window.cancelAnimationFrame(dragScrollFrameRef.current);
      dragScrollFrameRef.current = null;
    }

    dragClientYRef.current = null;
  }, []);

  useEffect(() => {
    if (!draggedBlockId) {
      stopDragAutoScroll();
      return;
    }

    const handleWindowDragOver = (event: globalThis.DragEvent) => {
      updateDragScrollPosition(event.clientY);
    };

    const tick = () => {
      const scrollContainer = findScrollContainer(stackRef.current);
      const clientY = dragClientYRef.current;

      if (scrollContainer && clientY !== null) {
        const rect =
          scrollContainer === document.scrollingElement
            ? { top: 0, bottom: window.innerHeight }
            : scrollContainer.getBoundingClientRect();
        const distanceFromTop = clientY - rect.top;
        const distanceFromBottom = rect.bottom - clientY;
        let deltaY = 0;

        if (distanceFromTop < DRAG_SCROLL_EDGE_SIZE) {
          deltaY =
            -((DRAG_SCROLL_EDGE_SIZE - Math.max(0, distanceFromTop)) / DRAG_SCROLL_EDGE_SIZE) *
            DRAG_SCROLL_MAX_PX_PER_FRAME;
        } else if (distanceFromBottom < DRAG_SCROLL_EDGE_SIZE) {
          deltaY =
            ((DRAG_SCROLL_EDGE_SIZE - Math.max(0, distanceFromBottom)) / DRAG_SCROLL_EDGE_SIZE) *
            DRAG_SCROLL_MAX_PX_PER_FRAME;
        }

        if (deltaY !== 0) {
          scrollContainer.scrollBy({ top: deltaY });
        }
      }

      dragScrollFrameRef.current = window.requestAnimationFrame(tick);
    };

    window.addEventListener("dragover", handleWindowDragOver);
    dragScrollFrameRef.current = window.requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("dragover", handleWindowDragOver);
      stopDragAutoScroll();
    };
  }, [draggedBlockId, stopDragAutoScroll, updateDragScrollPosition]);

  const createDragHandleProps = (blockId: Id<"blocks">) => ({
    draggable: true,
    onDragStart: (event: DragEvent<HTMLDivElement>) => {
      setDraggedBlockId(blockId);
      updateDragScrollPosition(event.clientY);
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("application/x-smart-view-block-id", blockId);
      event.dataTransfer.setData("text/plain", blockId);
      setBlockDragImage(event);
    },
    onDrag: (event: DragEvent<HTMLDivElement>) => {
      updateDragScrollPosition(event.clientY);
    },
    onDragEnd: () => {
      setDraggedBlockId(null);
      setDropTargetBlock(null);
      setDropTargetEmptyHalfBlockId(null);
      stopDragAutoScroll();
    },
  });

  const getSourceBlockId = (event?: DragEvent): Id<"blocks"> | null => {
    const sourceBlockId =
      draggedBlockId ??
      (event?.dataTransfer.getData("application/x-smart-view-block-id") as Id<"blocks">);

    return sourceBlockId || null;
  };

  const dropBlockAtRow = (targetRow: number) => {
    const sourceBlockId = getSourceBlockId();
    if (!sourceBlockId) return;

    void moveBlockToFullRow({
      smartViewId,
      sourceBlockId,
      targetRow,
    });
    setDraggedBlockId(null);
    setDropTargetBlock(null);
    setDropTargetEmptyHalfBlockId(null);
  };

  const dropBlockOnBlock = (event: DragEvent, targetBlockId: Id<"blocks">) => {
    event.preventDefault();
    event.stopPropagation();

    const sourceBlockId = getSourceBlockId(event);
    if (!sourceBlockId) return;

    const placement = getSmartViewBlockDropPlacement({
      sourceBlockId,
      targetBlockId,
      targetHorizontalRatio: getTargetHorizontalRatio(event),
      rows,
    });

    if (placement === "beforeBlock") {
      void moveBlockBefore({
        smartViewId,
        sourceBlockId,
        targetBlockId,
      });
    }

    if (placement === "aboveRow") {
      void moveBlockToFullRowBeforeBlock({
        smartViewId,
        sourceBlockId,
        targetBlockId,
      });
    }

    if (placement === "fullLeft" || placement === "fullRight") {
      void moveBlockBesideFullBlock({
        smartViewId,
        sourceBlockId,
        targetBlockId,
        side: placement === "fullLeft" ? "left" : "right",
      });
    }

    setDraggedBlockId(null);
    setDropTargetBlock(null);
    setDropTargetEmptyHalfBlockId(null);
  };

  const canDropOnEmptyHalfSlot = (event: DragEvent, targetBlockId: Id<"blocks">) => {
    const sourceBlockId = getSourceBlockId(event);

    return Boolean(
      sourceBlockId &&
        canDropBlockOnEmptyHalfSlot({
          sourceBlockId,
          targetBlockId,
          rows,
        })
    );
  };

  const dropBlockOnEmptyHalfSlot = (event: DragEvent, targetBlockId: Id<"blocks">) => {
    event.preventDefault();
    event.stopPropagation();

    const sourceBlockId = getSourceBlockId(event);
    if (!sourceBlockId) return;

    if (
      canDropBlockOnEmptyHalfSlot({
        sourceBlockId,
        targetBlockId,
        rows,
      })
    ) {
      void moveBlockToEmptyHalfSlot({
        smartViewId,
        sourceBlockId,
        targetBlockId,
      });
    }

    setDraggedBlockId(null);
    setDropTargetBlock(null);
    setDropTargetEmptyHalfBlockId(null);
  };

  if (layout.length === 0 && !isReadOnly) {
    return (
      <Stack>
        <SkeletonBlock variant="markdown" delay={0} />
        <SkeletonBlock variant="keyValue" delay={120} />
        <SkeletonBlock variant="taskList" delay={240} />
      </Stack>
    );
  }

  return (
    <Stack ref={stackRef}>
      {!isReadOnly && (
        <BlockInsertionRow
          onInsert={insertAt(0)}
          onAddDraft={() => addPlaceholderAt(0)}
          onDropBlock={() => dropBlockAtRow(0)}
          hideInsertButton={Boolean(draggedBlockId)}
        />
      )}
      {rows.map((row, idx) => {
        const rowKey = row.entries.map(entry => entry.block._id).join(":");
        const emptyHalfTargetBlockId = getSmartViewEmptyHalfSlotTargetBlockId(row);

        return (
          <BlockGroup key={`${row.rowIndex}:${rowKey}`}>
            <BlockRow>
              {row.entries.map(({ block, blockVersion, layoutEntry }) => {
                const width = Math.max(1, Math.min(layoutEntry.width, SMART_VIEW_LAYOUT_COLUMNS));

                if (block.isPlaceholder) {
                  return (
                    <BlockCell key={block._id} $width={width} $dropPlacement={null}>
                      <BlockDraft
                        initialDescription={block.placeholderDescription ?? ""}
                        onSubmit={text => submitPlaceholder(block._id, text)}
                        onCancel={() => cancelPlaceholder(block._id)}
                      />
                    </BlockCell>
                  );
                }

                if (!blockVersion) return null;
                const renderedTitle = blockVersion.title ?? block.title;
                const sourceNoteIds = collectBlockSourceNoteIds(blockVersion);

                return (
                  <BlockCell
                    key={block._id}
                    $width={width}
                    $dropPlacement={getDropPlacementForBlock(dropTargetBlock, block._id)}
                    onDragOver={event => {
                      if (isReadOnly) return;

                      updateDragScrollPosition(event.clientY);
                      const sourceBlockId = getSourceBlockId(event);
                      const placement = sourceBlockId
                        ? getSmartViewBlockDropPlacement({
                            sourceBlockId,
                            targetBlockId: block._id,
                            targetHorizontalRatio: getTargetHorizontalRatio(event),
                            rows,
                          })
                        : null;

                      if (!placement) return;

                      event.preventDefault();
                      event.dataTransfer.dropEffect = "move";
                      setDropTargetEmptyHalfBlockId(null);
                      setDropTargetBlock({ blockId: block._id, placement });
                    }}
                    onDragLeave={() => setDropTargetBlock(null)}
                    onDrop={event => dropBlockOnBlock(event, block._id)}
                  >
                    <BlockShell
                      smartViewId={smartViewId}
                      blockId={block._id}
                      title={renderedTitle}
                      prompt={blockVersion.prompt ?? block.prompt ?? ""}
                      iconKind={iconForBlockType(block.type)}
                      updatedAt={block.updatedAt}
                      isCollapsed={!isReadOnly && Boolean(block.isCollapsed)}
                      isReadOnly={isReadOnly}
                      dragHandleProps={!isReadOnly ? createDragHandleProps(block._id) : undefined}
                      isDragging={draggedBlockId === block._id}
                      sources={sourceNoteIds}
                      isUpdated={changedBlockIdSet.has(block._id)}
                      commentWorkItems={blockWorkItemState.get(block._id)?.commentWorkItems}
                      sourceSyncWorkItems={blockWorkItemState.get(block._id)?.sourceSyncWorkItems}
                    >
                      {renderBlockContent({
                        smartViewId,
                        blockId: block._id,
                        content: blockVersion.content,
                        isReadOnly,
                        blockTitle: renderedTitle,
                        notifyAgent,
                        sourceNoteIds,
                      })}
                    </BlockShell>
                  </BlockCell>
                );
              })}
              {!isReadOnly && emptyHalfTargetBlockId && (
                <EmptyHalfDropCell
                  $isDropTarget={dropTargetEmptyHalfBlockId === emptyHalfTargetBlockId}
                  onDragOver={event => {
                    updateDragScrollPosition(event.clientY);
                    if (!canDropOnEmptyHalfSlot(event, emptyHalfTargetBlockId)) return;

                    event.preventDefault();
                    event.dataTransfer.dropEffect = "move";
                    setDropTargetBlock(null);
                    setDropTargetEmptyHalfBlockId(emptyHalfTargetBlockId);
                  }}
                  onDragLeave={() => setDropTargetEmptyHalfBlockId(null)}
                  onDrop={event => dropBlockOnEmptyHalfSlot(event, emptyHalfTargetBlockId)}
                />
              )}
            </BlockRow>
            {!isReadOnly && (
              <BlockInsertionRow
                onInsert={insertAt(idx + 1)}
                onAddDraft={() => addPlaceholderAt(idx + 1)}
                onDropBlock={() => dropBlockAtRow(idx + 1)}
                hideInsertButton={Boolean(draggedBlockId)}
              />
            )}
          </BlockGroup>
        );
      })}
    </Stack>
  );
};

type KnownBlockContent = Doc<"blockVersions">["content"];
type RuntimeBlockContent = KnownBlockContent | { kind?: unknown; [key: string]: unknown };

const KNOWN_BLOCK_CONTENT_KINDS = new Set<string>([
  "markdown",
  "keyValue",
  "taskList",
  "table",
  "noteList",
  "noteShortcut",
  "expandedNote",
  "timeline",
  "dateTextLink",
  "collectionShortcut",
  "cardGrid",
  "singleImage",
  "multiImage",
  "resourceList",
  "resourceShortcut",
  "calendarEvents",
  "yearCalendar",
  "monthCalendar",
  "weekCalendar",
  "dayAgenda",
]);

export const renderBlockContent = ({
  smartViewId,
  blockId,
  content,
  isReadOnly,
  blockTitle,
  notifyAgent,
  sourceNoteIds = [],
}: {
  smartViewId?: string;
  blockId: Id<"blocks">;
  content: RuntimeBlockContent;
  isReadOnly: boolean;
  blockTitle: string;
  notifyAgent: (params: {
    blockId: string;
    blockTitle: string;
    summary: string;
    sourceNoteIds?: string[];
  }) => Promise<void>;
  sourceNoteIds?: string[];
}) => {
  const notify = (summary: string) => notifyAgent({ blockId, blockTitle, summary, sourceNoteIds });
  const requestAgentAction = smartViewId
    ? async (message: string) => {
        try {
          await chatWithSmartView({ smartViewId, message });
        } catch (err) {
          logger.error({
            message: "Smart View: failed to request agent action",
            info: { err: String(err) },
          });
        }
      }
    : undefined;

  if (!isKnownBlockContent(content)) {
    return <UnsupportedBlockContent kind={content.kind} />;
  }

  switch (content.kind) {
    case "markdown":
      return (
        <MarkdownBlock
          blockId={blockId}
          body={content.body}
          isReadOnly={isReadOnly}
          onUserAction={notify}
        />
      );
    case "keyValue":
      return (
        <KeyValueBlock
          blockId={blockId}
          pairs={content.pairs}
          isReadOnly={isReadOnly}
          onUserAction={notify}
        />
      );
    case "taskList":
      return (
        <TaskListBlock
          blockId={blockId}
          tasks={content.tasks}
          sections={content.sections}
          isReadOnly={isReadOnly}
          onUserAction={notify}
        />
      );
    case "table":
      return (
        <TableBlock
          blockId={blockId}
          columns={content.columns}
          rows={content.rows}
          rowSources={content.rowSources}
          sections={content.sections}
          isReadOnly={isReadOnly}
          onUserAction={notify}
        />
      );
    case "noteList":
      return <NoteListBlock notes={content.notes} />;
    case "noteShortcut":
      return <NoteShortcutBlock note={content.note} />;
    case "expandedNote":
      return <ExpandedNoteBlock note={content.note} />;
    case "timeline":
      return <TimelineBlock items={content.items} />;
    case "dateTextLink":
      return <DateTextLinkBlock items={content.items} />;
    case "collectionShortcut":
      return <CollectionShortcutBlock collection={content.collection} />;
    case "cardGrid":
      return <CardGridBlock cards={content.cards} />;
    case "singleImage":
      return <SingleImageBlock image={content.image} />;
    case "multiImage":
      return <MultiImageBlock images={content.images} />;
    case "resourceList":
      return <ResourceListBlock resources={content.resources} />;
    case "resourceShortcut":
      return <ResourceShortcutBlock resource={content.resource} />;
    case "calendarEvents":
      return (
        <CalendarEventsBlock
          events={content.events}
          newNoteLabel={content.newNoteLabel}
          isReadOnly={isReadOnly}
          onRequestAgentAction={requestAgentAction}
        />
      );
    case "yearCalendar":
      return <YearCalendarBlock year={content.year} events={content.events} />;
    case "monthCalendar":
      return <MonthCalendarBlock month={content.month} events={content.events} />;
    case "weekCalendar":
      return <WeekCalendarBlock weekStart={content.weekStart} events={content.events} />;
    case "dayAgenda":
      return (
        <DayAgendaBlock
          date={content.date}
          events={content.events}
          isReadOnly={isReadOnly}
          onRequestAgentAction={requestAgentAction}
        />
      );
  }
};

const isKnownBlockContent = (content: RuntimeBlockContent): content is KnownBlockContent =>
  typeof content.kind === "string" && KNOWN_BLOCK_CONTENT_KINDS.has(content.kind);

const collectBlockSourceNoteIds = (blockVersion: Doc<"blockVersions">): string[] => {
  const sourceIds = new Set<string>();
  addSourceIds(sourceIds, blockVersion.sources);
  collectContentSourceNoteIds(sourceIds, blockVersion.content);
  return Array.from(sourceIds);
};

const addSourceIds = (sourceIds: Set<string>, values?: string[]) => {
  for (const value of values ?? []) {
    if (value.trim().length > 0) {
      sourceIds.add(value);
    }
  }
};

const addOptionalSourceId = (sourceIds: Set<string>, value?: string) => {
  if (value && value.trim().length > 0) {
    sourceIds.add(value);
  }
};

const collectContentSourceNoteIds = (sourceIds: Set<string>, content: KnownBlockContent) => {
  if (content.kind === "markdown") {
    const matches = content.body.match(/\[\^([A-Za-z0-9_-]+)\]/g) ?? [];
    for (const match of matches) {
      addOptionalSourceId(sourceIds, match.slice(2, -1));
    }
  }

  if (content.kind === "keyValue") {
    for (const pair of content.pairs) {
      addSourceIds(sourceIds, pair.sources);
    }
  }

  if (content.kind === "taskList") {
    for (const task of content.tasks) {
      addSourceIds(sourceIds, task.sources);
    }
  }

  if (content.kind === "table") {
    for (const rowSources of content.rowSources ?? []) {
      addSourceIds(sourceIds, rowSources);
    }
    for (const row of content.rows) {
      if (!Array.isArray(row)) {
        addSourceIds(sourceIds, row.sources);
      }
    }
  }

  if (content.kind === "noteList") {
    for (const note of content.notes) {
      addOptionalSourceId(sourceIds, note.noteId);
      addSourceIds(sourceIds, note.sources);
    }
  }

  if (content.kind === "noteShortcut" || content.kind === "expandedNote") {
    addOptionalSourceId(sourceIds, content.note.noteId);
    addSourceIds(sourceIds, content.note.sources);
  }

  if (content.kind === "timeline") {
    for (const item of content.items) {
      addSourceIds(sourceIds, item.sources);
    }
  }

  if (content.kind === "dateTextLink") {
    for (const item of content.items) {
      addSourceIds(sourceIds, item.sources);
    }
  }

  if (content.kind === "collectionShortcut") {
    addSourceIds(sourceIds, content.collection.sources);
  }

  if (content.kind === "cardGrid") {
    for (const card of content.cards) {
      addSourceIds(sourceIds, card.sources);
    }
  }

  if (content.kind === "singleImage") {
    addSourceIds(sourceIds, content.image.sources);
  }

  if (content.kind === "multiImage") {
    for (const image of content.images) {
      addSourceIds(sourceIds, image.sources);
    }
  }

  if (content.kind === "resourceList") {
    for (const resource of content.resources) {
      addOptionalSourceId(sourceIds, resource.noteId);
      addSourceIds(sourceIds, resource.sources);
    }
  }

  if (content.kind === "resourceShortcut") {
    addOptionalSourceId(sourceIds, content.resource.noteId);
    addSourceIds(sourceIds, content.resource.sources);
  }

  if (
    content.kind === "calendarEvents" ||
    content.kind === "yearCalendar" ||
    content.kind === "monthCalendar" ||
    content.kind === "weekCalendar" ||
    content.kind === "dayAgenda"
  ) {
    for (const event of content.events) {
      addOptionalSourceId(sourceIds, event.noteId);
      addSourceIds(sourceIds, event.sources);
    }
  }
};

export const UnsupportedBlockContent: FC<{ kind: unknown }> = ({ kind }) => {
  const label = typeof kind === "string" && kind.trim() ? kind : "unknown";

  return (
    <UnsupportedContent role="status" data-smart-view-unsupported-block>
      Unsupported block type: {label}
    </UnsupportedContent>
  );
};

const getDropPlacementForBlock = (
  dropTargetBlock: {
    blockId: Id<"blocks">;
    placement: SmartViewBlockDropPlacement;
  } | null,
  blockId: Id<"blocks">
): SmartViewBlockDropPlacement | null => {
  if (dropTargetBlock?.blockId !== blockId) {
    return null;
  }

  return dropTargetBlock.placement;
};

export const buildBlockWorkItemState = (workItems: Doc<"smartViewWorkItems">[]) => {
  const stateByBlockId = new Map<
    string,
    {
      commentWorkItems: BlockStatusWorkItem[];
      sourceSyncWorkItems: BlockStatusWorkItem[];
    }
  >();

  for (const item of workItems) {
    if (!item.blockId) {
      continue;
    }

    const current = stateByBlockId.get(item.blockId) ?? {
      commentWorkItems: [],
      sourceSyncWorkItems: [],
    };

    if (item.kind === "blockComment" && item.status !== "resolved") {
      current.commentWorkItems.push(toBlockStatusWorkItem(item));
    }

    if (item.kind === "sourceSync" && item.status !== "resolved") {
      current.sourceSyncWorkItems.push(toBlockStatusWorkItem(item));
    }

    stateByBlockId.set(item.blockId, current);
  }

  stateByBlockId.forEach(state => {
    state.commentWorkItems.sort(sortWorkItemsNewestFirst);
    state.sourceSyncWorkItems.sort(sortWorkItemsNewestFirst);
  });

  return stateByBlockId;
};

const toBlockStatusWorkItem = (item: Doc<"smartViewWorkItems">): BlockStatusWorkItem => ({
  id: item._id,
  status: item.status,
  content: item.content,
  errorMessage: item.errorMessage,
  createdAt: item.createdAt,
});

const sortWorkItemsNewestFirst = (a: BlockStatusWorkItem, b: BlockStatusWorkItem) =>
  b.createdAt - a.createdAt;

const findScrollContainer = (element: HTMLElement | null): HTMLElement | null => {
  let current = element?.parentElement ?? null;

  while (current) {
    const { overflowY } = window.getComputedStyle(current);
    const canScroll = current.scrollHeight > current.clientHeight;

    if (canScroll && ["auto", "scroll", "overlay"].includes(overflowY)) {
      return current;
    }

    current = current.parentElement;
  }

  return document.scrollingElement as HTMLElement | null;
};

const getTargetHorizontalRatio = (event: DragEvent<Element>): number => {
  const rect = event.currentTarget.getBoundingClientRect();

  if (rect.width === 0) {
    return 0.5;
  }

  return (event.clientX - rect.left) / rect.width;
};

const setBlockDragImage = (event: DragEvent<HTMLElement>) => {
  const card = event.currentTarget.closest("[data-smart-view-block-card]");

  if (!(card instanceof HTMLElement)) {
    return;
  }

  const rect = card.getBoundingClientRect();
  const dragImage = card.cloneNode(true) as HTMLElement;
  dragImage.style.position = "fixed";
  dragImage.style.top = "-10000px";
  dragImage.style.left = "-10000px";
  dragImage.style.width = `${rect.width}px`;
  dragImage.style.height = `${rect.height}px`;
  dragImage.style.overflow = "hidden";
  dragImage.style.opacity = "0.72";
  dragImage.style.pointerEvents = "none";
  dragImage.style.transform = "none";
  document.body.appendChild(dragImage);
  event.dataTransfer.setDragImage(dragImage, Math.min(32, rect.width / 2), 24);
  window.setTimeout(() => dragImage.remove(), 0);
};

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing["6"]} ${({ theme }) => theme.spacing["8"]}
    ${({ theme }) => theme.spacing["10"]};
  gap: ${({ theme }) => theme.spacing["4"]};
`;

const BlockGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const BlockRow = styled.div`
  display: grid;
  grid-template-columns: repeat(${SMART_VIEW_LAYOUT_COLUMNS}, minmax(0, 1fr));
  gap: ${({ theme }) => theme.spacing["4"]};
  align-items: stretch;
`;

const BlockCell = styled.div<{
  $width: number;
  $dropPlacement: SmartViewBlockDropPlacement | null;
}>`
  position: relative;
  grid-column: span ${({ $width }) => $width};
  min-width: 0;
  border-radius: ${({ theme }) => theme.borderRadius.large};

  &::before {
    content: "";
    position: absolute;
    z-index: 2;
    pointer-events: none;
    opacity: ${({ $dropPlacement }) => ($dropPlacement ? 1 : 0)};
    background: ${({ theme }) => theme.colors.border.emphasis};
    border-radius: 999px;
    transition: opacity 0.15s ease;
    ${({ $dropPlacement }) =>
      $dropPlacement === "beforeBlock" || $dropPlacement === "fullLeft"
        ? `
          top: 0;
          bottom: 0;
          left: -8px;
          width: 2px;
        `
        : $dropPlacement === "fullRight"
          ? `
          top: 0;
          right: -8px;
          bottom: 0;
          width: 2px;
        `
          : `
          top: -8px;
          left: 0;
          right: 0;
          height: 2px;
        `}
  }

  @media (max-width: 900px) {
    grid-column: 1 / -1;
  }
`;

const EmptyHalfDropCell = styled.div<{ $isDropTarget: boolean }>`
  position: relative;
  grid-column: span ${SMART_VIEW_HALF_WIDTH};
  min-width: 0;
  border-radius: ${({ theme }) => theme.borderRadius.large};

  &::before {
    content: "";
    position: absolute;
    inset: 0 auto 0 -8px;
    width: 2px;
    border-radius: 999px;
    background: ${({ theme }) => theme.colors.border.emphasis};
    opacity: ${({ $isDropTarget }) => ($isDropTarget ? 1 : 0)};
    transition: opacity 0.15s ease;
  }

  @media (max-width: 900px) {
    display: none;
  }
`;

const UnsupportedContent = styled.div`
  border: 1px dashed rgba(15, 23, 42, 0.18);
  border-radius: 8px;
  color: rgba(15, 23, 42, 0.62);
  font-size: 13px;
  line-height: 18px;
  padding: 12px 14px;
`;
